/**
 * Shared helper to open a user's profile from Group (and other) surfaces.
 * - Own GUID → My Profile
 * - Other GUID → View Profile
 * - Missing/invalid GUID → toast, no navigation
 */
import { ROUTES } from '../constants';
import { ToastService } from '../platform/notifier';
import i18n from '../i18n';

const INVALID_GUID_TOKENS = new Set(['', 'undefined', 'null', 'nan']);

export const isValidUserGuid = (value: unknown): value is string => {
  if (value == null) return false;
  const guid = String(value).trim();
  if (!guid || INVALID_GUID_TOKENS.has(guid.toLowerCase())) return false;
  // Reject numeric-only IDs / usernames that are not profile GUIDs.
  if (/^\d+$/.test(guid)) return false;
  return true;
};

export const normalizeUserGuid = (value: unknown): string | null => {
  if (!isValidUserGuid(value)) return null;
  return String(value).trim();
};

export const resolveProfileUserGuidFromRecord = (
  record: Record<string, unknown> | null | undefined,
): string | null => {
  if (!record || typeof record !== 'object') return null;

  return (
    normalizeUserGuid(record.profileUserGuid) ??
    normalizeUserGuid(record.userGuid) ??
    null
  );
};

type NavigateFn = {
  navigate: (...args: any[]) => void;
};

export interface NavigateToUserProfileOptions {
  navigation: NavigateFn;
  /** Target user's profile GUID (userGuid / profileUserGuid). */
  userGuid?: string | null;
  /** Current logged-in user's GUID for self-vs-other routing. */
  currentUserGuid?: string | null;
  /** Optional toast override when GUID is missing/invalid. */
  missingGuidMessage?: string;
}

/**
 * Navigate to My Profile or View Profile based on the target GUID.
 * Returns true when navigation was performed.
 */
export const navigateToUserProfile = ({
  navigation,
  userGuid,
  currentUserGuid,
  missingGuidMessage,
}: NavigateToUserProfileOptions): boolean => {
  const targetGuid = normalizeUserGuid(userGuid);
  if (!targetGuid) {
    ToastService.show({
      type: 'error',
      message:
        missingGuidMessage || i18n.t('profile.toast.unableToLoadProfile'),
    });
    return false;
  }

  const selfGuid = normalizeUserGuid(currentUserGuid);
  if (selfGuid && selfGuid.toLowerCase() === targetGuid.toLowerCase()) {
    navigation.navigate(ROUTES.MY_PROFILE);
    return true;
  }

  navigation.navigate(ROUTES.VIEW_PROFILE, {
    profileUserGuid: targetGuid,
  });
  return true;
};

export default navigateToUserProfile;
